import React from 'react';
import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getPleatType } from '../../../helpers/pleatType';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { throwError } from '../../../helpers/throwError';
import { ProductBuilderMachineEvent } from '../../../types';
import {
  Selector,
  SelectorImageItem,
  SelectorItem,
} from '../../Common/Selector';
import { SectionContent } from './SectionContent';
import { Heading } from '../../Common/Heading';
import { Caption } from '../../Common/Caption';
import { getProductStateConfig } from '../../../helpers/getProductStateConfig';
import { getBaseProductType } from '../../../helpers/getBaseProductType';
import { CurtainHelpModal } from '../CurtainHelpModal';
import { HelpTooltip } from '../../Icon';
import styled from 'styled-components';

const StyledHelpIcon = styled.span`
  width: 42px;
  height: 42px;
  padding-left: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

type OnClick = (
  pleatType: string,
  send: (event: ProductBuilderMachineEvent) => void,
) => () => void;

const onClick: OnClick = (pleatType, send) => () =>
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      pleatType,
    },
  });

export const PleatType = () => {
  const [state, send] = useService(productBuilderMachine);
  const { pleatType } = state.context.custom;
  const productType = getBaseProductType(state.context);
  const pleatTypeOptions = getPleatType(state.context);
  const [isHelpModalOpen, setIsHelpModalOpen] = React.useState(false);

  // Only show help modal for curtains
  const isCurtain = state.context.products[0]?.type === 'Curtain';

  if (isCurrentState('pleatType', state) && pleatTypeOptions.length === 0) {
    throwError(state);
  }

  const openHelpModal = () => {
    if (isCurtain) setIsHelpModalOpen(true);
  };

  const closeHelpModal = () => {
    setIsHelpModalOpen(false);
  };

  return (
    <SectionContent>
      <StyledHeadingWrapper>
        <Heading>Pleat Type</Heading>
        {isCurtain && (
          <StyledHelpIcon onClick={openHelpModal}>
            <HelpTooltip />
          </StyledHelpIcon>
        )}
      </StyledHeadingWrapper>
      <Selector isSlider={true}>
        {pleatTypeOptions.map(option => {
          const isSelected = option === pleatType;
          const { showIcon, Icon, description } = getProductStateConfig(
            productType,
            'pleatType',
            option,
          );
          return (
            <SelectorImageItem
              isSelected={isSelected}
              onClick={onClick(option, send)}
              showIcon={showIcon}
              Icon={Icon}
              title={option}
              key={option}
              description={description}
            />
          );
        })}
      </Selector>
      {isCurtain && (
        <CurtainHelpModal
          modalIsOpen={isHelpModalOpen}
          closeModal={closeHelpModal}
          stepType="pleatType"
        />
      )}
    </SectionContent>
  );
};
